/**
 * Inventory Items Management Hook
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 2: Inventory Items Management
 *
 * Provides hooks for:
 * - Fetching inventory items with stock levels
 * - Creating new items
 * - Updating existing items
 * - Archiving items (soft delete)
 * - Filtering by category
 * - Getting low stock items
 */

import { useState, useEffect, useCallback, useMemo } from 'react'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/inventory-items`

/**
 * Call the inventory-items edge function
 */
async function callInventoryItemsFunction(action, data = {}) {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result.data
}

/**
 * Main hook for inventory items management
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.activeOnly - If true, only fetch active items (default: false)
 * @returns {Object} - items, isLoading, error, lowStockItems, createItem, updateItem, archiveItem, refetch, filterByCategory
 */
export function useInventoryItems(options = {}) {
  const { activeOnly = false } = options

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState('all')

  /**
   * Fetch inventory items from the API
   */
  const fetchItems = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const params = activeOnly ? { active: true } : {}
      const data = await callInventoryItemsFunction('list', params)

      setItems(data || [])
    } catch (err) {
      console.error('Error fetching inventory items:', err)
      setError(err.message)
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }, [activeOnly])

  /**
   * Get items filtered by category
   */
  const filteredItems = useMemo(() => {
    if (categoryFilter === 'all') {
      return items
    }
    return items.filter(item => item.category === categoryFilter)
  }, [items, categoryFilter])

  /**
   * Get low stock items (items at or below reorder point)
   */
  const lowStockItems = useMemo(() => {
    return items.filter(item => item.is_low_stock && item.active)
  }, [items])

  /**
   * Get item counts by category
   */
  const categoryCounts = useMemo(() => {
    const counts = {
      all: items.length,
      raw_material: 0,
      packaging: 0,
      sundry: 0,
      other: 0,
    }

    for (const item of items) {
      if (counts[item.category] !== undefined) {
        counts[item.category]++
      }
    }

    return counts
  }, [items])

  /**
   * Create a new inventory item
   *
   * @param {Object} itemData - Item data
   * @param {string} itemData.name - Item name (required)
   * @param {string} itemData.batch_prefix - Batch prefix (required)
   * @param {string} itemData.category - Category (required)
   * @param {string} itemData.unit_of_measure - Unit of measure (required)
   * @param {string} itemData.purchase_unit - Purchase unit
   * @param {number} itemData.conversion_factor - Conversion factor
   * @param {number} itemData.reorder_point - Reorder point
   * @param {number} itemData.lead_time_days - Lead time in days
   * @param {string} itemData.preferred_supplier_id - Supplier ID
   * @param {string} itemData.notes - Additional notes
   * @returns {Promise<Object>} - Created item
   */
  const createItem = useCallback(async (itemData) => {
    try {
      setError(null)
      const newItem = await callInventoryItemsFunction('create', itemData)

      // Add to local state
      setItems(prev => [...prev, newItem].sort((a, b) =>
        a.name.localeCompare(b.name)
      ))

      return newItem
    } catch (err) {
      console.error('Error creating inventory item:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Update an existing inventory item
   *
   * @param {string} id - Item ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated item
   */
  const updateItem = useCallback(async (id, updates) => {
    try {
      setError(null)
      const updatedItem = await callInventoryItemsFunction('update', { id, ...updates })

      // Update local state
      setItems(prev =>
        prev.map(item => item.id === id ? updatedItem : item)
          .sort((a, b) => a.name.localeCompare(b.name))
      )

      return updatedItem
    } catch (err) {
      console.error('Error updating inventory item:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Archive an inventory item (soft delete)
   *
   * @param {string} id - Item ID
   * @returns {Promise<Object>} - Archived item
   */
  const archiveItem = useCallback(async (id) => {
    try {
      setError(null)
      const archivedItem = await callInventoryItemsFunction('delete', { id })

      // Update local state - if activeOnly, remove from list; otherwise update
      if (activeOnly) {
        setItems(prev => prev.filter(item => item.id !== id))
      } else {
        setItems(prev =>
          prev.map(item => item.id === id ? archivedItem : item)
        )
      }

      return archivedItem
    } catch (err) {
      console.error('Error archiving inventory item:', err)
      setError(err.message)
      throw err
    }
  }, [activeOnly])

  /**
   * Restore an archived inventory item
   *
   * @param {string} id - Item ID
   * @returns {Promise<Object>} - Restored item
   */
  const restoreItem = useCallback(async (id) => {
    try {
      setError(null)
      const restoredItem = await callInventoryItemsFunction('update', { id, active: true })

      // Update local state
      setItems(prev =>
        prev.map(item => item.id === id ? restoredItem : item)
          .sort((a, b) => a.name.localeCompare(b.name))
      )

      return restoredItem
    } catch (err) {
      console.error('Error restoring inventory item:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Filter items by category
   *
   * @param {string} category - Category to filter by ('all', 'raw_material', 'packaging', 'sundry', 'other')
   */
  const filterByCategory = useCallback((category) => {
    setCategoryFilter(category)
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  return {
    items: filteredItems,
    allItems: items,
    isLoading,
    error,
    lowStockItems,
    categoryCounts,
    categoryFilter,
    createItem,
    updateItem,
    archiveItem,
    restoreItem,
    refetch: fetchItems,
    filterByCategory,
  }
}

/**
 * Hook for fetching a single inventory item
 *
 * @param {string} itemId - Item ID
 * @returns {Object} - item, isLoading, error, refetch
 */
export function useInventoryItem(itemId) {
  const [item, setItem] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchItem = useCallback(async () => {
    if (!itemId) {
      setItem(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const data = await callInventoryItemsFunction('get', { id: itemId })
      setItem(data)
    } catch (err) {
      console.error('Error fetching inventory item:', err)
      setError(err.message)
      setItem(null)
    } finally {
      setIsLoading(false)
    }
  }, [itemId])

  useEffect(() => {
    fetchItem()
  }, [fetchItem])

  return { item, isLoading, error, refetch: fetchItem }
}

/**
 * Hook for fetching low stock items only
 *
 * @returns {Object} - lowStockItems, isLoading, error, refetch
 */
export function useLowStockItems() {
  const [lowStockItems, setLowStockItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchLowStockItems = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await callInventoryItemsFunction('low-stock')
      setLowStockItems(data || [])
    } catch (err) {
      console.error('Error fetching low stock items:', err)
      setError(err.message)
      setLowStockItems([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLowStockItems()
  }, [fetchLowStockItems])

  return { lowStockItems, isLoading, error, refetch: fetchLowStockItems }
}

export default useInventoryItems
